import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import { Fancybox } from "@fancyapps/ui/src/Fancybox";
import ApplicationController from "./application_controller";
import { loadGoogleCharts, drawChartFromData, loadXLSXLibrary } from "../utils/charts";

export default class extends ApplicationController {
  static targets = [
    "grid",
    "chip",
    "current_app",
    "widgetCategory",
    "searchInput",
    "noResultMessage",
  ];

  static values = {
    url: String,
    dashId: Number,
    currentApp: String,
    popupMessageUrl: String,
  };

  connect() {
    this.changeColor();

    // ⚠️ évite de re-brancher 10 fois les handlers si Turbo/Stimulus reconnect
    this._isConnected = true;

    this.selectedChips = new Set();
    this.chipTargets.forEach((chip) => {
      if (chip.classList.contains("selected")) {
        this.selectedChips.add(chip.dataset.id);
      }
    });

    if (this.hasPopupMessageUrlValue) {
      this.getPopupMessage();
    }

    // --- init grid ---
    if (this.hasGridTarget) this.initGridStack();

    // ❌ CAUSE DU BUG : compact au connect => ça remonte tout à chaque refresh
    // if (this.hasGridTarget && this.grid) this.compactAllWidgetsUp();

    // --- ton mode "dashloader" (je le laisse, mais sans re-init grid inutilement) ---
    if (!this.hasUrlValue || !this.hasDashIdValue) {
      // console.warn("⚠️ dashloader: url or dashId is missing, skipping AJAX.");
    } else {
      const url = this.urlValue;

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          this.element.innerHTML = html;

          // Recharge Stimulus si besoin
          this.reconnectStimulusControllers(this.element);

          // Re-init GridStack seulement si nécessaire
          if (this.hasGridTarget) this.initGridStack();

          this.applyStickyTableHeaders(this.element);

          const eventName = `dash:load_${this.dashIdValue}`;
          document.dispatchEvent(new CustomEvent(eventName));
        })
        .catch((error) => {
          this.element.innerHTML =
            "<div class='alert alert-danger'>Erreur lors du chargement du widget.</div>";
          console.error("❌ Erreur AJAX dashloader :", error);
        });
    }

    // --- listeners globaux : bind + store pour pouvoir remove() dans disconnect ---
    this._onReflexDone = this.handleReflexDone.bind(this);
    document.addEventListener("dashboard:reflex-done", this._onReflexDone);

    this._onWidgetContentUpdated = this.onWidgetContentUpdated.bind(this);
    document.addEventListener(
      "widget:content-updated",
      this._onWidgetContentUpdated,
    );
  }
  disconnect() {
    if (this._onReflexDone) {
      document.removeEventListener("dashboard:reflex-done", this._onReflexDone);
      this._onReflexDone = null;
    }
    if (this._onWidgetContentUpdated) {
      document.removeEventListener(
        "widget:content-updated",
        this._onWidgetContentUpdated,
      );
      this._onWidgetContentUpdated = null;
    }

    // ✅ si la grille existe toujours dans le DOM, on NE la détruit PAS
    const gridStillInDom =
      this.grid?.el && document.body.contains(this.grid.el);
    if (gridStillInDom) return;

    // ✅ sinon, on peut cleanup
    if (this.grid) {
      if (window.myDashboard?.grid === this.grid)
        window.myDashboard.grid = null;
      try {
        this.grid.destroy(false);
      } catch (e) {}
      this.grid = null;
    }
  }

  initGridStack() {
    if (!this.hasGridTarget) {
      console.info("Il n'y a pas de gridStack dans le DOM");
      return;
    }

    // ✅ GUARD : si déjà init, ne pas ré-init (ça multiplie les listeners et les re-layout)
    if (this.grid && this.grid.el === this.gridTarget) return;

    // si une ancienne instance existe (ex: reconnect), on destroy proprement
    if (this.grid) {
      try {
        this.grid.destroy(false);
      } catch (e) {}
      this.grid = null;
    }

    this.grid = GridStack.init(
      {
        handle: ".drag-handle",
        cellHeight: 5,
        animate: true,
        float: true,
        resizable: { handles: "all" },
      },
      this.gridTarget,
    );

    window.myDashboard = {
      grid: this.grid,
      currentPage: parseInt(
        new URLSearchParams(window.location.search).get("page") || 1,
      ),
      dashId: this.dashIdValue,
    };

    // 🔒 Flag pour éviter boucle : update() programmatique => "change" => save => etc.
    this._isInternalGridUpdate = false;

    this.grid.on("change", (event, items) => {
      // console.log(
      //   "CHANGE OK",
      //   items.map((i) => i.id)
      // );
      // console.log("change called, internal =", this._isInternalGridUpdate);

      // =====================================================
      // ✅ REDRAW CHARTS UNIQUEMENT
      // =====================================================
      items.forEach((item) => {
        const graphContainer = item.el.querySelector("[data-chart-data]");
        if (!graphContainer) return; // ⛔️ NON-CHART → on sort immédiatement

        const colCount = this.grid.opts.column;
        const gridWidth = this.grid.el.clientWidth;
        const cellWidth = gridWidth / colCount;

        const contentWrapper = item.el.querySelector(
          ".content.table-scroll-wrapper",
        );

        const gridStackItemContentHeight = this.getFullHeight(
          item.el.querySelector(".grid-stack-item-content"),
        );

        const extendTitleHeight = this.getFullHeight(
          item.el.querySelector(".extend_title"),
        );

        const barreTitreHeight = this.getFullHeight(
          item.el.querySelector(".barre-titre"),
        );

        const moreDetailsBlockHeight = item.el.querySelector(
          "[data-chart-more-details]",
        )
          ? 44
          : 0;

        const chartToolsHeight = item.el.querySelector(
          "[data-export-csv-label]",
        )
          ? 26
          : 0;

        const contentWrapperGap =
          chartToolsHeight +
          moreDetailsBlockHeight +
          barreTitreHeight +
          extendTitleHeight;

        const finalHeight =
          gridStackItemContentHeight - contentWrapperGap - 26 - 36;

        // 🔥 IMPORTANT : wrapper réglé AVANT le draw
        if (contentWrapper) {
          contentWrapper.style.height = `${
            gridStackItemContentHeight -
            barreTitreHeight -
            extendTitleHeight -
            36
          }px`;
        }

        loadGoogleCharts(() =>
          drawChartFromData(graphContainer, {
            chartWidth: item.w * cellWidth - 60,
            chartHeight: finalHeight,
            dashId: this.dashIdValue,
          }),
        );
      });

      // =====================================================
      // ⛔ BLOQUER UNIQUEMENT LA SAUVEGARDE
      // =====================================================
      if (this._isInternalGridUpdate) return;

      const payload = items.map((item) => ({
        id: item.el.dataset.id,
        grid_config: {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          auto_position: item.autoPosition || false,
          no_resize: item.noResize || false,
          no_move: item.noMove || false,
        },
      }));

      fetch(`/${this.currentAppValue}/fdashboards/update_all_settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
            .content,
        },
        body: JSON.stringify({ settings: payload }),
      });
    });

    this.grid.on("dragstart", (_, el) => {
      el.classList.add("dragging-simulated");
      document.querySelectorAll(".grid-stack-item").forEach((item) => {
        if (item !== el) item.classList.add("highlight-background");
      });
    });

    this.grid.on("dragstop", (_, el) => {
      el.classList.remove("dragging-simulated");
      el.dataset.userMoved = "true";
      document
        .querySelectorAll(".grid-stack-item")
        .forEach((item) => item.classList.remove("highlight-background"));
    });

    this.grid.on("resizestart", (_, el) => {
      el.classList.add("resizing-simulated");
      el.style.removeProperty("height");
      document.querySelectorAll(".grid-stack-item").forEach((item) => {
        if (item !== el) item.classList.add("highlight-background");
      });
    });

    this.grid.on("resizestop", (_, el) => {
      el.classList.remove("resizing-simulated");
      el.style.removeProperty("height");
      el.dataset.userResized = "true";
      document
        .querySelectorAll(".grid-stack-item")
        .forEach((item) => item.classList.remove("highlight-background"));
    });

    this.toggleGridStackItems();
  }

  // ==========================================================
  // handler pour event venant du widgetloader
  // => Resize "safe" SANS y+1/y-1, SANS compact global
  // ==========================================================
  onWidgetContentUpdated(arg) {
    if (!this.grid) return;
    //1) Déterminer settingId / itemEl selon le type d'argument
    let settingId = null;
    let itemEl = null;
    // cas: arg est un élément .grid-stack-item
    if (arg?.classList?.contains("grid-stack-item")) {
      itemEl = arg;
      settingId = itemEl.dataset.id || null;
    }
    // cas: arg est un ID (number/string)
    else if (typeof arg === "string" || typeof arg === "number") {
      settingId = String(arg);
    }
    // cas: arg est un event (CustomEvent)
    else if (arg?.detail?.settingId) {
      settingId = String(arg.detail.settingId);
    }
    if (!itemEl && settingId) {
      itemEl = this.grid.el.querySelector(
        `.grid-stack-item[data-id='${settingId}']`,
      );
    }
    // console.log("🔔 widget:content-updated →", { settingId, itemEl });
    if (!itemEl) return;
    // 1) CHART: on laisse le redraw au pipeline "change"
    if (itemEl.querySelector("[data-chart-data]")) {
      this.miniResizeFull(itemEl);
      return;
    }
    // 2) NON-CHART: seulement si auto-resize ON
    const autoInput =
      itemEl.querySelector("input[data-auto-resize]") ||
      itemEl._fxConfigPanel?.querySelector("input[data-auto-resize]");
    const autoVal = autoInput
      ? parseInt(autoInput.dataset.autoResize || "2", 10)
      : 2;
    if (autoVal !== 1) return;
    // éviter de relancer 10 fois pendant un filtre qui déclenche plusieurs updates
    if (itemEl.dataset._autoResizeBusy === "1") return;
    itemEl.dataset._autoResizeBusy = "1";
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          // A) resize full
          this.resizeToContentFull({ currentTarget: itemEl });
          // B) compact en dessous (uniquement pour ce widget)
          const node = this.grid.engine.nodes.find((n) => n.el === itemEl);
          if (node) {
            // anti-boucle compact
            if (!this._isCompacting) {
              this._isCompacting = true;
              try {
                const { targets, changed } = this.compactYOnlyWidgetsBelow(
                  this.grid,
                  node,
                );
                this.saveNodesSettings([node, ...targets], {
                  reason: "auto-resize+compact",
                });
              } finally {
                this._isCompacting = false;
              }
            }
          }
        } finally {
          // libère le lock
          itemEl.dataset._autoResizeBusy = "0";
        }
      });
    });
  }
  // ✅ Sauvegarde toute la grille (snapshot) via update_all_settings
  saveNodesSettings(nodes, { reason = "moved-nodes" } = {}) {
    const grid = this.grid || window.myDashboard?.grid;
    if (!grid?.engine?.nodes) return;

    const uniq = new Map();

    (nodes || []).forEach((n) => {
      // accepte node GridStack OU element
      const node = n?.el ? n : grid.engine.nodes.find((x) => x.el === n);

      if (!node?.el?.dataset?.id) return;

      const id = node.el.dataset.id;
      uniq.set(id, {
        id,
        grid_config: {
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h,
          auto_position: node.autoPosition || false,
          no_resize: node.noResize || false,
          no_move: node.noMove || false,
        },
      });
    });

    const settings = [...uniq.values()];
    if (!settings.length) return;

    // console.log("💾 saveNodesSettings", { reason, count: settings.length });

    fetch(`/${this.currentAppValue}/fdashboards/update_all_settings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
          ?.content,
      },
      body: JSON.stringify({ settings }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      })
      .catch((err) => console.error("❌ saveNodesSettings error:", err));
  }

  miniResizeFull(arg) {
    const itemEl = arg?.classList?.contains("grid-stack-item")
      ? arg
      : arg?.currentTarget?.closest?.(".grid-stack-item") ||
        arg?.target?.closest?.(".grid-stack-item");

    const grid = this.grid || window.myDashboard?.grid;
    if (!itemEl || !grid) return;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) return;

    const originalW = node.w;
    const originalH = node.h;

    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();

      // micro resize réel (déclenche change)
      grid.update(itemEl, { w: originalW, h: originalH + 1 });
      grid.update(itemEl, { w: originalW, h: originalH });

      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }
  }

  // Code mort : jamais appelée (aucun data-action, aucun appel interne). Commentée le
  // 2026-07-18 pour vérification avant suppression définitive.
  /*
  resizeItemToContentSafe(itemEl) {
    const grid = window.myDashboard?.grid;
    if (!grid || !itemEl) return;

    const widgetId = itemEl.dataset.id;
    const container = itemEl.querySelector(".dash_container");
    const contentWrapper = itemEl.querySelector(
      ".content.table-scroll-wrapper",
    );

    if (!container || !widgetId) return;

    const contentSize = this.getContentSize(contentWrapper);
    const extendTitleHeight = this.getFullHeight(
      itemEl.querySelector(".extend_title"),
    );
    const barreTitreHeight = this.getFullHeight(
      itemEl.querySelector(".barre-titre"),
    );

    const cellHeight = grid.getCellHeight(true);
    const colCount = grid.opts.column;
    const gridWidth = grid.el.clientWidth;
    const cellWidth = gridWidth / colCount;

    const containerHeight =
      barreTitreHeight + extendTitleHeight + (contentSize?.height || 0) + 36 ||
      container.offsetHeight;
    const contentWidth = contentSize?.width || container.offsetWidth;

    const newHeight = Math.max(1, Math.ceil(containerHeight / cellHeight));
    let newWidth = Math.min(
      colCount,
      Math.max(1, Math.ceil(contentWidth / cellWidth)),
    );
    if (contentWidth > newWidth * cellWidth && newWidth < colCount)
      newWidth += 1;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) return;

    // ✅ update SANS toucher y (donc pas de glissement)
    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();
      grid.update(itemEl, {
        width: newWidth,
        height: newHeight,
        x: node.x,
        y: node.y,
      });
      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }

    // persist (même logique que chez toi)
    const finalNode = grid.engine.nodes.find((n) => n.el === itemEl) || node;
    const config = {
      x: finalNode.x,
      y: finalNode.y,
      w: finalNode.w,
      h: finalNode.h,
      auto_position: finalNode.autoPosition || false,
      no_resize: finalNode.noResize || false,
      no_move: finalNode.noMove || false,
    };

    fetch(`/${this.currentAppValue}/fdashboards/${widgetId}/update_setting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ grid_config: config }),
    }).catch(() => {});
  }
  */

  // -------------------------
  // Le reste : inchangé (je garde tout ton code)
  // -------------------------

  searchWidgets(event) {
    const normalize = (str) =>
      str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    const query = normalize(event.target.value);
    const chips = this.element.querySelectorAll(".chip");
    const categories = this.element.querySelectorAll(".widget-category");
    let totalVisibleChips = 0;

    chips.forEach((chip) => {
      const text = normalize(chip.textContent);
      const isVisible = text.includes(query);
      chip.style.display = isVisible ? "" : "none";
      if (isVisible) totalVisibleChips++;
    });

    categories.forEach((category) => {
      const visibleChips = category.querySelectorAll(
        '.chip:not([style*="display: none"])',
      );
      category.style.display = visibleChips.length > 0 ? "" : "none";
    });

    if (this.hasNoResultMessageTarget) {
      this.noResultMessageTarget.style.display =
        totalVisibleChips === 0 ? "flex" : "none";
    }
  }

  toggleGridStackItems() {
    document
      .querySelectorAll(".grid-stack-item")
      .forEach((e) => e.classList.remove("fx-d-none"));
    const skeleton = document.getElementById("fx-widgets-skeleton");
    skeleton?.classList.add("fx-d-none");
    skeleton?.classList.remove("fx-d-flex");
  }

  changePage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const page = event.currentTarget.dataset.page;
    this.stimulate("FdashboardReflex#change_page", page);
  }

  handleReflexDone(event) {
    const { id, page, widgetid, reflex } = event.detail;

    if (reflex === "dash_setting#destroy") {
      const el = document.querySelector(`.grid-stack-item[data-id='${id}']`);
      if (el && this.grid) {
        var fx_wid_count_page = document.getElementById(
          `fx_wid_count_page_${page}`,
        );
        var fx_wid_chip = document.getElementById(`fx_wid_chip_${widgetid}`);
        var fx_wid_chip_page_number = document.getElementById(
          `fx_wid_chip_page_number_${widgetid}`,
        );
        var fx_wid_chip_add_to_page = document.getElementById(
          `fx_wid_chip_add_to_page_${widgetid}`,
        );
        var fx_wid_chip_move_to_page = document.getElementById(
          `fx_wid_chip_move_to_page_${widgetid}`,
        );

        if (fx_wid_count_page) {
          let count = parseInt(fx_wid_count_page.textContent, 10);
          if (!isNaN(count) && count > 1) {
            fx_wid_count_page.textContent = count - 1;
          } else {
            fx_wid_count_page.classList.add("fx-d-none");
          }
        }

        if (fx_wid_chip) fx_wid_chip.classList.remove("selected");
        if (fx_wid_chip_add_to_page)
          fx_wid_chip_add_to_page.classList.remove("fx-d-none");
        if (fx_wid_chip_page_number)
          fx_wid_chip_page_number.classList.add("fx-d-none");
        if (fx_wid_chip_move_to_page)
          fx_wid_chip_move_to_page.classList.add("fx-d-none");

        this.grid.removeWidget(el);
        // ne plus compacter après suppression
      }
    }
  }

  moveToPage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const widgetId = event.currentTarget.dataset.id;
    const targetPage = event.currentTarget.dataset.targetPage;
    const settingId = event.currentTarget.dataset.settingId;
    const current_app = event.currentTarget.dataset.currentApp;
    const currentPage = event.currentTarget.dataset.currentPage;
    const sourcePage = event.currentTarget.dataset.sourcePage;
    const graphType = event.currentTarget.dataset.graphType;
    const dash_page = event.currentTarget.dataset.dashPage;

    const fx_wid_move_to_page_btn = document.getElementById(
      `fx_wid_move_to_page_btn_${targetPage}`,
    );
    const fx_wid_move_to_page_btn_loader = document.getElementById(
      `fx_wid_move_to_page_btn_loader_${targetPage}`,
    );
    const fx_wid_chip_page_number = document.getElementById(
      `fx_wid_chip_page_number_${widgetId}`,
    );

    // je recipere le chip dès le départ, avant que le DOM ne bouge
    const chip = event.currentTarget.closest(".chip");

    if (fx_wid_move_to_page_btn)
      fx_wid_move_to_page_btn.classList.add("fx-d-none");
    if (fx_wid_move_to_page_btn_loader)
      fx_wid_move_to_page_btn_loader.classList.remove("fx-d-none");

    fetch(`/${current_app}/fdashboards/move_widget`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({
        dash_library_id: widgetId,
        new_page: targetPage,
        setting_id: settingId,
      }),
    })
      .then((res) => {
        if (fx_wid_move_to_page_btn)
          fx_wid_move_to_page_btn.classList.remove("fx-d-none");
        if (fx_wid_move_to_page_btn_loader)
          fx_wid_move_to_page_btn_loader.classList.add("fx-d-none");

        if (!res.ok) {
          console.error("❌ move_widget: requête échouée");
          return;
        }
        return res.json();
      })
      .then((data) => {
        if (!data || !chip) return;

        if (fx_wid_chip_page_number) {
          fx_wid_chip_page_number.textContent = "page " + targetPage;
        }

        const el = document.querySelector(
          `.grid-stack-item[data-id='${settingId}']`,
        );
        if (el && window.myDashboard?.grid) {
          window.myDashboard.grid.removeWidget(el);
        }

        const sourceCount = document.getElementById(
          `fx_wid_count_page_${sourcePage}`,
        );
        const targetCount = document.getElementById(
          `fx_wid_count_page_${targetPage}`,
        );

        if (sourceCount) {
          let count = parseInt(sourceCount.textContent, 10);
          if (!isNaN(count) && count > 1) {
            sourceCount.textContent = count - 1;
          } else {
            sourceCount.classList.add("fx-d-none");
          }
        }

        if (targetCount) {
          let count = parseInt(targetCount.textContent, 10);
          if (!isNaN(count)) {
            targetCount.textContent = count + 1;
            targetCount.classList.remove("fx-d-none");
          }
        }

        // Réorganise dropdown localement dans le chip déjà capturé
        const sourceDropdownItem = chip
          .querySelector(`#fx_wid_move_to_page_btn_${sourcePage}`)
          ?.closest(".dropdown-item");
        const targetDropdownItem = chip
          .querySelector(`#fx_wid_move_to_page_btn_${targetPage}`)
          ?.closest(".dropdown-item");

        if (sourceDropdownItem)
          sourceDropdownItem.classList.remove("fx-d-none");
        if (targetDropdownItem) targetDropdownItem.classList.add("fx-d-none");

        // Met à jour tous les boutons de ce dropdown
        const dropdown = chip.querySelector(".fx-dropdown-content");
        if (dropdown) {
          dropdown
            .querySelectorAll(`[id^='fx_wid_move_to_page_btn_']`)
            .forEach((link) => {
              link.dataset.sourcePage = targetPage;
            });
        }

        if (data.status === "ok" && currentPage === targetPage) {
          this.addToTargetPage(
            widgetId,
            current_app,
            data.setting_id,
            data.grid_config,
            graphType,
            dash_page,
          );
        }
      });
  }

  addToTargetPage(
    dashLibraryId,
    current_app,
    setting_id,
    grid_config,
    graphType,
    dash_page,
  ) {
    fetch(
      `/${current_app}/fdashboards/widget_card?id=${dashLibraryId}&graph_type=${graphType}&dash_page=${dash_page}`,
    )
      .then((res) => res.text())
      .then((html) => {
        const el = document.createElement("div");
        el.classList.add("grid-stack-item");
        el.dataset.id = setting_id;

        el.setAttribute("gs-x", grid_config.x);
        el.setAttribute("gs-y", grid_config.y);
        el.setAttribute("gs-w", grid_config.w);
        el.setAttribute("gs-h", grid_config.h);
        el.setAttribute("gs-auto-position", grid_config.auto_position);
        el.setAttribute("gs-no-resize", grid_config.no_resize);
        el.setAttribute("gs-no-move", grid_config.no_move);

        el.innerHTML = `
          <div class="grid-stack-item-content card shadow-sm p-2" id="grid-stack-item-content-${dashLibraryId}">
            ${html}
          </div>
        `;

        if (window.myDashboard?.grid) {
          window.myDashboard.grid.el.appendChild(el);
          window.myDashboard.grid.makeWidget(el);
        }

        $("#cross_widget table").addClass(
          "fx-table-hm cross fx-table-hm-wrapped fx-table-12",
        );
        $('#cross_widget input[type="submit"]').addClass("mt-2 fx-button");
      });
  }

  guardDisabled(event) {
    const container = event.currentTarget.closest('[aria-disabled="true"]');
    if (container && container.getAttribute("aria-disabled") === "true") {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
  }

  addToPage(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const dashLibraryId = event.currentTarget.dataset.id;
    const dash_page = event.currentTarget.dataset.dashPage;

    const num_page = event.currentTarget.dataset.targetPage;
    const current_page = event.currentTarget.dataset.currentPage;
    const current_app = event.currentTarget.dataset.currentApp;
    const graphType = event.currentTarget.dataset.graphType;
    const fx_wid_count_target_page = document.getElementById(
      `fx_wid_count_page_${num_page}`,
    );
    const fx_wid_count_current_page = document.getElementById(
      `fx_wid_count_page_${current_page}`,
    );
    const fx_wid_chip_page_number = document.getElementById(
      `fx_wid_chip_page_number_${dashLibraryId}`,
    );
    const fx_wid_chip_add_to_page = document.getElementById(
      `fx_wid_chip_add_to_page_${dashLibraryId}`,
    );
    const fx_wid_chip_move_to_page = document.getElementById(
      `fx_wid_chip_move_to_page_${dashLibraryId}`,
    );
    const fx_wid_chip = document.getElementById(`fx_wid_chip_${dashLibraryId}`);

    const fx_wid_add_to_page_btn = document.getElementById(
      `fx_wid_add_to_page_btn_${num_page}`,
    );
    const fx_wid_add_to_page_btn_loader = document.getElementById(
      `fx_wid_add_to_page_btn_loader_${num_page}`,
    );

    if (fx_wid_add_to_page_btn) {
      fx_wid_add_to_page_btn.classList.add("fx-d-none");
    }

    if (fx_wid_add_to_page_btn_loader) {
      fx_wid_add_to_page_btn_loader.classList.remove("fx-d-none");
    }

    fetch(`/${current_app}/fdashboards/add_widget`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
      },
      body: JSON.stringify({
        dash_library_id: dashLibraryId,
        num_page: num_page,
        dash_page: dash_page,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const { setting_id, grid_config } = data;
        return fetch(
          `/${current_app}/fdashboards/widget_card?id=${dashLibraryId}&graph_type=${graphType}&dash_page=${dash_page}`,
        )
          .then((res) => res.text())
          .then((html) => {
            const el = document.createElement("div");
            el.classList.add("grid-stack-item");
            el.dataset.id = setting_id;

            el.setAttribute("gs-x", grid_config.x);
            el.setAttribute("gs-y", grid_config.y);
            el.setAttribute("gs-w", grid_config.w);
            el.setAttribute("gs-h", grid_config.h);
            el.setAttribute("gs-auto-position", grid_config.auto_position);
            el.setAttribute("gs-no-resize", grid_config.no_resize);
            el.setAttribute("gs-no-move", grid_config.no_move);

            el.innerHTML = `
          <div class="grid-stack-item-content card shadow-sm p-2" id="grid-stack-item-content-${dashLibraryId}">
           
                        ${html} 
          </div>
        `;

            // console.log("elllll", el);
            // console.log("num_page", num_page);
            // console.log("num_page", num_page);
            if (fx_wid_add_to_page_btn) {
              fx_wid_add_to_page_btn.classList.remove("fx-d-none");
            }

            if (fx_wid_add_to_page_btn_loader) {
              fx_wid_add_to_page_btn_loader.classList.add("fx-d-none");
            }

            if (num_page == current_page) {
              if (window.myDashboard?.grid)
                window.myDashboard.grid.makeWidget(el);
            } else {
            }

            if (fx_wid_count_target_page) {
              let count = parseInt(fx_wid_count_target_page.textContent, 10);
              if (!isNaN(count)) {
                fx_wid_count_target_page.textContent = count + 1;
                fx_wid_count_target_page.classList.remove("fx-d-none");
              }
            }

            if (fx_wid_chip) {
              fx_wid_chip.classList.add("selected");
            }

            if (fx_wid_chip_add_to_page) {
              fx_wid_chip_add_to_page.classList.add("fx-d-none");
            }

            if (fx_wid_chip_page_number) {
              fx_wid_chip_page_number.classList.remove("fx-d-none");
              fx_wid_chip_page_number.textContent = "page " + num_page;
            }

            if (fx_wid_chip_move_to_page) {
              fx_wid_chip_move_to_page.classList.remove("fx-d-none");

              // Mise à jour de tous les <a> dans le dropdown
              const dropdownLinks = fx_wid_chip_move_to_page.querySelectorAll(
                "a[data-target-page]",
              );
              dropdownLinks.forEach((link) => {
                link.dataset.sourcePage = num_page;
                link.dataset.settingId = setting_id;
              });
              // Cacher le bouton move_to_page "Déplacer vers la page (page_actuelle)" dans le dropdown
              const sourceDropdownItem = fx_wid_chip_move_to_page
                .querySelector(`#fx_wid_move_to_page_btn_${num_page}`)
                ?.closest(".dropdown-item");
              if (sourceDropdownItem)
                sourceDropdownItem.classList.add("fx-d-none");
            }

            $("#cross_widget table").addClass(
              "fx-table-hm cross fx-table-hm-wrapped fx-table-12",
            );
            $('#cross_widget input[type="submit"]').addClass("mt-2 fx-button");
          });
      });
  }
  compact() {
    if (this.grid) {
      this.compactYOnly(this.grid);
    }
  }

  openWidgetSelection(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (typeof Fancybox !== "undefined") {
      Fancybox.show([{ src: "#widget-selection", type: "inline" }]);
    } else {
      console.warn("⚠️ Fancybox non chargé !");
    }
  }

  setFixedHeight(arg, fixedPx = 250) {
    // ✅ Par défaut: arg = event. Si arg est itemEl -> on ignore event.
    const itemEl = arg?.classList?.contains("grid-stack-item")
      ? arg
      : arg?.currentTarget?.closest?.(".grid-stack-item") ||
        arg?.target?.closest?.(".grid-stack-item");

    const grid = this.grid || window.myDashboard?.grid;
    if (!itemEl || !grid) {
      console.warn("⛔️ Pas d’élément grid-stack ou GridStack non dispo.");
      return;
    }

    // Charts : anim + exit
    if (itemEl.querySelector("[data-chart-data]")) {
      itemEl.classList.add("resizing-simulated");
      setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
      itemEl.style.removeProperty("height");
      return;
    }

    const widgetId = itemEl.dataset.id;
    const container = itemEl.querySelector(".dash_container");
    const contentWrapper = itemEl.querySelector(
      ".content.table-scroll-wrapper",
    );
    if (!container || !widgetId) {
      console.warn("⛔️ Container ou widgetId manquant.");
      return;
    }

    const contentSize = this.getContentSize(contentWrapper);
    const extendTitleHeight = this.getFullHeight(
      itemEl.querySelector(".extend_title"),
    );
    const barreTitreHeight = this.getFullHeight(
      itemEl.querySelector(".barre-titre"),
    );

    const cellHeight = grid.getCellHeight(true);
    const colCount = grid.opts.column;
    const gridWidth = grid.el.clientWidth;
    const cellWidth = gridWidth / colCount;

    const contentWrapperMarginTop =
      parseFloat(getComputedStyle(contentWrapper).marginTop) || 0;

    // hauteur "réelle" du contenu, puis on force au max fixedPx
    // Marge de sécurité 45 : a montré 0 résidu de scroll sur la plupart des tests en
    // conditions réelles (bouton, pas simulation) sur dash_214, mais 7px de résidu est
    // apparu au moins une fois dans cette même session (probablement lié à un léger
    // écart de rendu de la ligne de filtres). 50 est plus sûr (0 résidu à chaque essai)
    // au prix d'un espace blanc un peu plus visible sous le contenu. Revérifier en
    // conditions réelles avant de redescendre davantage.
    const containerHeight =
      barreTitreHeight +
        extendTitleHeight +
        contentWrapperMarginTop +
        (contentSize?.height || 0) +
        45 || container.offsetHeight;

    const contentWidth = contentSize?.width || container.offsetWidth;

    const forcedContainerHeight = Math.min(containerHeight, fixedPx);
    const newH = Math.max(1, Math.ceil(forcedContainerHeight / cellHeight));

    // largeur recalculée
    let newW = Math.min(
      colCount,
      Math.max(1, Math.ceil(contentWidth / cellWidth)),
    );
    if (contentWidth > newW * cellWidth && newW < colCount) newW += 1;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) {
      console.warn("⛔️ Node GridStack introuvable.");
      return;
    }

    // Rien à faire si déjà identique
    if (node.w === newW && node.h === newH) return;

    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();

      itemEl.setAttribute("gs-auto-position", "false");

      grid.update(itemEl, {
        x: node.x,
        y: node.y,
        w: newW,
        h: newH,
      });

      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }

    // Persist (prend le node final)

    const finalNode = grid.engine.nodes.find((n) => n.el === itemEl) || node;
    const config = {
      x: finalNode.x,
      y: finalNode.y,
      w: finalNode.w,
      h: finalNode.h,
      auto_position: finalNode.autoPosition || false,
      no_resize: finalNode.noResize || false,
      no_move: finalNode.noMove || false,
    };

    fetch(`/${this.currentAppValue}/fdashboards/${widgetId}/update_setting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ grid_config: config }),
    }).catch((err) => console.error("❌ Erreur fetch:", err));

    // Anim
    itemEl.classList.add("resizing-simulated");
    setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
    itemEl.style.removeProperty("height");
    this.applyStickyTableHeaders(itemEl);

    const nsode = this.grid.engine.nodes.find((n) => n.el === itemEl);
    if (nsode) {
      // anti-boucle compact
      if (!this._isCompacting) {
        this._isCompacting = true;
        try {
          const { targets, changed } = this.compactYOnlyWidgetsBelow(
            this.grid,
            nsode,
          );

          this.saveNodesSettings([nsode, ...targets], {
            reason: "auto-resize+compact",
          });
        } finally {
          this._isCompacting = false;
        }
      }
    }
  }

  resizeToContentFull(arg) {
    // arg = event (DOM) par défaut. Si arg est un itemEl -> on ignore event.
    const itemEl = arg?.classList?.contains("grid-stack-item")
      ? arg
      : arg?.currentTarget?.closest?.(".grid-stack-item") ||
        arg?.target?.closest?.(".grid-stack-item");

    const grid = this.grid || window.myDashboard?.grid;
    if (!itemEl || !grid) return;

    // charts : pas de resize ici
    if (itemEl.querySelector("[data-chart-data]")) {
      itemEl.classList.add("resizing-simulated");
      setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
      itemEl.style.removeProperty("height");
      return;
    }

    const widgetId = itemEl.dataset.id;
    const container = itemEl.querySelector(".dash_container");
    const contentWrapper = itemEl.querySelector(
      ".content.table-scroll-wrapper",
    );
    if (!container || !widgetId) return;

    const getFullHeight = (el) => {
      if (!el) return 0;
      const cs = window.getComputedStyle(el);
      return (
        el.offsetHeight +
        parseFloat(cs.marginTop || 0) +
        parseFloat(cs.marginBottom || 0)
      );
    };
    const getContentSize = (el) => {
      if (!el)
        return { width: container.offsetWidth, height: container.offsetHeight };
      // Mesurer el.firstElementChild (au lieu de el lui-même) donnait un
      // résultat faux dès que le premier enfant n'est pas le vrai contenu
      // visuel : les widgets non-tableau injectent souvent un <script> (ou
      // un élément inline sans hauteur) avant leur contenu réel, donnant
      // scrollHeight=0 et faisant retomber resize-to-content/auto à une
      // taille minimale qui cache tout le contenu. el.scrollHeight/Width
      // capture le contenu réel quel que soit le premier enfant, sans rien
      // changer pour les widgets tableau (où le wrapper n'a qu'un seul
      // enfant, donc même résultat qu'avant).
      return { width: el.scrollWidth, height: el.scrollHeight };
    };

    const contentSize = getContentSize(contentWrapper);
    const extendTitleHeight = getFullHeight(
      itemEl.querySelector(".extend_title"),
    );
    const barreTitreHeight = getFullHeight(
      itemEl.querySelector(".barre-titre"),
    );

    const cellHeight = grid.getCellHeight(true);
    const colCount = grid.opts.column;
    const gridWidth = grid.el.clientWidth;
    const cellWidth = gridWidth / colCount;

    const contentWrapperMarginTop =
      parseFloat(getComputedStyle(contentWrapper).marginTop) || 0;

    // Marge de sécurité 45 : cf. setFixedHeight ci-dessus.
    const containerHeight =
      barreTitreHeight +
        extendTitleHeight +
        contentWrapperMarginTop +
        (contentSize?.height || 0) +
        45 || container.offsetHeight;
    const contentWidth = contentSize?.width || container.offsetWidth;

    const newH = Math.max(1, Math.ceil(containerHeight / cellHeight));
    let newW = Math.min(
      colCount,
      Math.max(1, Math.ceil(contentWidth / cellWidth)),
    );
    if (contentWidth > newW * cellWidth && newW < colCount) newW += 1;

    const node = grid.engine.nodes.find((n) => n.el === itemEl);
    if (!node) return;

    // rien à faire
    if (node.w === newW && node.h === newH) return;

    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();

      itemEl.setAttribute("gs-auto-position", "false");

      // ✅ IMPORTANT : GridStack = w/h (pas width/height)
      grid.update(itemEl, {
        x: node.x,
        y: node.y,
        w: newW,
        h: newH,
      });

      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }

    // persist
    const finalNode = grid.engine.nodes.find((n) => n.el === itemEl) || node;
    const config = {
      x: finalNode.x,
      y: finalNode.y,
      w: finalNode.w,
      h: finalNode.h,
      auto_position: finalNode.autoPosition || false,
      no_resize: finalNode.noResize || false,
      no_move: finalNode.noMove || false,
    };

    fetch(`/${this.currentAppValue}/fdashboards/${widgetId}/update_setting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ grid_config: config }),
    }).catch(() => {});

    itemEl.classList.add("resizing-simulated");
    setTimeout(() => itemEl.classList.remove("resizing-simulated"), 300);
    itemEl.style.removeProperty("height");
    this.applyStickyTableHeaders(itemEl);

    if (itemEl.dataset._autoResizeBusy === "1") return;
    itemEl.dataset._autoResizeBusy = "1";

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          // B) compact en dessous (uniquement pour ce widget)

          if (finalNode) {
            // anti-boucle compact
            if (!this._isCompacting) {
              this._isCompacting = true;
              try {
                const { targets, changed } = this.compactYOnlyWidgetsBelow(
                  this.grid,
                  finalNode,
                );

                this.saveNodesSettings([finalNode, ...targets], {
                  reason: "auto-resize+compact",
                });
              } finally {
                this._isCompacting = false;
              }
            }
          }
        } finally {
          itemEl.dataset._autoResizeBusy = "0";
        }
      });
    });
  }

  // Code mort : jamais appelée. Commentée le 2026-07-18 pour vérification avant
  // suppression définitive.
  /*
  getElementHeight(parent, selector, defaultHeight = 0) {
    const element = parent.querySelector(selector);
    if (element) return defaultHeight;
    return 0;
  }
  */

  compactYOnly(grid) {
    if (!grid?.engine?.nodes) return;

    // 🧩 Tri par colonne, puis par Y croissant
    const sorted = grid.engine.nodes
      .slice()
      .sort((a, b) => a.x - b.x || a.y - b.y);

    sorted.forEach((node) => {
      let newY = 0;
      // On remonte tant qu’il y a une collision
      while (
        grid.engine.collide(node, { x: node.x, y: newY, w: node.w, h: node.h })
      ) {
        newY++;
      }

      if (newY !== node.y) {
        // console.log(
        //   `⬆️ Widget ${node.el?.dataset?.id} moved y=${node.y} → y=${newY}`
        // );
        grid.engine.moveNode(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        });
      }
    });

    grid._triggerChangeEvent();
    grid._updateStyles(true);
  }

  compactYOnlyWidgetsBelow(grid, refNode) {
    // console.group("🧲 compactYOnlyWidgetsBelow");

    if (!grid?.engine?.nodes?.length || !refNode) {
      console.warn("⛔️ Grid or refNode not available.");
      // console.groupEnd();
      return { targets: [], changed: [] };
    }

    // --- snapshot BEFORE ---
    const before = new Map();
    grid.engine.nodes.forEach((n) => {
      const id = n?.el?.dataset?.id;
      if (!id) return;
      before.set(id, { x: n.x, y: n.y, w: n.w, h: n.h });
    });

    const nodes = grid.engine.nodes;

    const refX1 = refNode.x;
    const refX2 = refNode.x + refNode.w - 1;
    let refBottom = refNode.y + refNode.h;

    // 🎯 cibles : sous ref + overlap horizontal
    const targets = nodes
      .filter(
        (n) =>
          n !== refNode &&
          n.y >= refBottom &&
          n.x <= refX2 &&
          n.x + n.w - 1 >= refX1,
      )
      .sort((a, b) => a.y - b.y || a.x - b.x);

    // console.log("ref:", {
    //   id: refNode.el?.dataset?.id,
    //   x: refNode.x,
    //   y: refNode.y,
    //   w: refNode.w,
    //   h: refNode.h,
    // });
    // console.log(
    //   "targets:",
    //   targets.map((n) => n.el?.dataset?.id)
    // );

    // --- compact ---
    this._isInternalGridUpdate = true;
    try {
      grid.batchUpdate();

      for (const node of targets) {
        let newY = refBottom;
        let guard = 0;

        while (
          grid.engine.collide(node, {
            x: node.x,
            y: newY,
            w: node.w,
            h: node.h,
          }) &&
          guard++ < 2000
        )
          newY++;

        if (guard >= 2000) {
          console.warn("⚠️ guard hit for node", node.el?.dataset?.id);
        }

        if (newY !== node.y) {
          grid.engine.moveNode(node, {
            x: node.x,
            y: newY,
            w: node.w,
            h: node.h,
          });
        }

        refBottom = Math.max(refBottom, newY + node.h);
      }

      grid.commit();
    } finally {
      this._isInternalGridUpdate = false;
    }

    if (typeof grid._updateStyles === "function") grid._updateStyles(true);

    // --- snapshot AFTER + diff => changed (cascade incluse) ---
    const changed = [];
    grid.engine.nodes.forEach((n) => {
      const id = n?.el?.dataset?.id;
      if (!id) return;
      const b = before.get(id);
      if (!b) return;

      if (b.x !== n.x || b.y !== n.y || b.w !== n.w || b.h !== n.h) {
        changed.push(n);
      }
    });

    // console.log(
    //   "✅ changed nodes:",
    //   changed.map((n) => n.el?.dataset?.id)
    // );

    console.groupEnd();
    return { targets, changed };
  }

  compactAllWidgetsUp() {
    if (!this.grid?.engine?.nodes) {
      console.warn("⛔️ Grid not available.");
      return;
    }

    // 🧩 Tri stable : par colonne, puis par Y croissant
    const sorted = this.grid.engine.nodes
      .slice()
      .sort((a, b) => a.x - b.x || a.y - b.y);

    sorted.forEach((node) => {
      let newY = 0;

      // On cherche la première position libre vers le haut
      while (
        this.grid.engine.collide(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        })
      ) {
        newY++;
      }

      if (newY !== node.y) {
        // console.log(
        //   `⬆️ Widget ${node.el?.dataset?.id} moved from y=${node.y} → y=${newY}`
        // );
        this.grid.engine.moveNode(node, {
          x: node.x,
          y: newY,
          w: node.w,
          h: node.h,
        });
      }
    });

    // console.log("🔄 Triggering change + updating styles (global compact up)");
    this.grid._triggerChangeEvent();
    this.grid._updateStyles(true);
  }
  reconnectStimulusControllers(container) {
    container.querySelectorAll("[data-controller]").forEach((el) => {
      const controllers = el.getAttribute("data-controller").split(" ");
      controllers.forEach((controller) => {
        application
          .getControllerForElementAndIdentifier(el, controller)
          ?.connect();
      });
    });
  }

  getPopupMessage() {
    fetch(this.popupMessageUrlValue, {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const popup = document.getElementById("popup-content");

        if (popup) {
          popup.innerHTML = html;

          // Vérifie si #fx_modal existe dans le contenu injecté
          const modal = popup.querySelector("#fx_modal");

          if (modal) {
            Fancybox.show([{ src: "#popup-content", type: "inline" }]);
          }
        }
      })
      .catch((error) => {
        console.error("Erreur lors du chargement du popup :", error);
      });
  }
 toggleConfigDropdown(event) {
  event.preventDefault();
  event.stopPropagation();

    const dashId = event.currentTarget.dataset.dashIdParam;
    const panel = document.getElementById(`config-dropdown-panel-${dashId}`);
    if (!panel) return;

    const isOpen = panel._fxIsOpen === true;

    // Ferme tous les autres dropdowns ouverts (widgets et menu "Nouveautés" confondus),
    // y compris d'éventuels panneaux orphelins d'un widget re-rendu entre temps
    // (filtre/checklist) pendant qu'un panneau était ouvert : voir closeConfigDropdown.
    document.querySelectorAll(".config-dropdown-panel").forEach((p) => {
      this.closeConfigDropdown(p);
    });

    if (!isOpen) {
      this.openConfigDropdown(panel, event.currentTarget);

      // Ferme au clic en dehors (une seule fois)
      const closeOnOutsideClick = (e) => {
        if (!panel.contains(e.target) && e.target !== event.currentTarget) {
          this.closeConfigDropdown(panel);
          document.removeEventListener("click", closeOnOutsideClick);
        }
      };
      // setTimeout pour éviter que ce clic-ci ne déclenche immédiatement la fermeture
      setTimeout(() => {
        document.addEventListener("click", closeOnOutsideClick);
      }, 0);
    }
  }

  // .grid-stack-item-content a overflow-y:auto/overflow-x:hidden (règles de gridstack.js) :
  // nécessaire pour clipper/scroller le contenu d'un widget réduit en hauteur, donc on n'y
  // touche jamais (un essai précédent le désactivait temporairement, ce qui affichait aussi
  // tout le contenu du widget normalement masqué par ce clip — régression).
  // Seuls les panneaux réellement dans un widget gridstack (donc réellement clippés) sont
  // basculés en position:fixed le temps qu'ils sont ouverts, pour les extraire de ce clip.
  // Ceux qui n'y sont pas (ex: le menu "Nouveautés", qui réutilise ce même toggle avec
  // dash-id-param="tours-menu") ne sont jamais touchés : comportement d'origine inchangé.
  // Le reparentage vise le plus proche ancêtre data-controller="fdashboard" — jamais
  // <body> — pour rester dans le périmètre Stimulus attendu par les data-action du
  // panneau (toggleAutoresize/toggleFiltrageAuto), sinon elles cessent de se déclencher.
  openConfigDropdown(panel, anchor) {
    panel._fxIsOpen = true;
    panel.style.display = "block";

    const clippingWrap = panel.closest(".grid-stack-item-content");
    if (!clippingWrap) return;

    if (!panel._fxOriginalParent) {
      panel._fxOriginalParent = panel.parentNode;
      panel._fxOriginalNextSibling = panel.nextSibling;
    }
    // toggleAutoresize/toggleFiltrageAuto retrouvent leur widget via
    // checkbox.closest(".grid-stack-item") ; une fois le panneau sorti de
    // .grid-stack-item-content cette ascendance n'existe plus, donc on la mémorise ici.
    // Référence inverse aussi : d'autres endroits (onWidgetContentUpdated ici,
    // getUserAutoResize/setUserAutoResize/applyEffectiveUI dans widgetloader_controller.js)
    // font itemEl.querySelector("input[data-auto-resize]") en partant du widget — sans ce
    // pointeur, ces lectures échouent silencieusement tant que le panneau est détaché
    // (ex: le réajustement automatique à l'activation du toggle ne se déclenchait plus).
    if (!panel._fxGridStackItem) {
      panel._fxGridStackItem = panel.closest(".grid-stack-item");
    }
    if (panel._fxGridStackItem) {
      panel._fxGridStackItem._fxConfigPanel = panel;
    }

    const scopeRoot = anchor.closest('[data-controller="fdashboard"]') || document.body;
    scopeRoot.appendChild(panel);
    panel.style.position = "fixed";
    panel.style.margin = "0";
    panel.style.zIndex = "3000";

    const margin = 8;
    const rect = anchor.getBoundingClientRect();
    const panelWidth = panel.offsetWidth || 220;

    let left = rect.right - panelWidth;
    left = Math.max(margin, Math.min(left, window.innerWidth - panelWidth - margin));

    let top = rect.bottom + 4;
    const panelHeight = panel.offsetHeight || 0;
    if (top + panelHeight > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - panelHeight - 4);
    }

    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
    panel.style.right = "auto";
  }

  closeConfigDropdown(panel) {
    panel.style.display = "none";
    panel._fxIsOpen = false;

    if (!panel._fxOriginalParent) return; // jamais déplacé (pas dans un widget)

    if (document.contains(panel._fxOriginalParent)) {
      panel._fxOriginalParent.insertBefore(panel, panel._fxOriginalNextSibling);
    } else {
      // Le widget d'origine a été re-rendu (filtre, checklist...) pendant que ce panneau
      // était détaché : son ancien parent n'existe plus, un panneau frais a été généré à
      // l'intérieur du nouveau widget. Celui-ci est donc orphelin.
      panel.remove();
    }

    panel._fxOriginalParent = null;
    panel._fxOriginalNextSibling = null;
    panel.style.position = "";
    panel.style.left = "";
    panel.style.top = "";
    panel.style.right = "";
    panel.style.zIndex = "";
    panel.style.margin = "";
  }

  // --- Export PDF / XLSX du widget ---
  // Icônes dans l'en-tête du widget (voir _main_dash_ajax.erb), câblées sur fdashboard
  // (et non widgetloader) car data-controller="fdashboard" est le seul ancêtre commun
  // aux icônes de l'en-tête ET au contenu du widget lui-même.

  loadHtml2Canvas(callback) {
    if (typeof html2canvas !== "undefined") return callback();
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.3.2/html2canvas.min.js";
    script.onload = callback;
    document.head.appendChild(script);
  }

  loadJsPDF(callback) {
    if (window.jspdf?.jsPDF) return callback();
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    script.onload = callback;
    document.head.appendChild(script);
  }

  getWidgetExportFilename(widgetEl) {
    // .title est la cellule du titre ; .barre-titre est la table englobante qui
    // contient AUSSI le groupe d'icônes cog/pdf/xlsx/drag/adjust (reparenté dedans
    // par widgetloader_controller.js#connect), donc .title doit être tenté en premier
    // sous peine de récupérer tout le texte des tooltips/boutons dans le nom de fichier.
    const titleText =
      widgetEl?.querySelector(".title")?.textContent?.trim() ||
      widgetEl?.querySelector(".barre-titre")?.textContent?.trim() ||
      "widget";
    return titleText.replace(/[\/\\:*?"<>|]/g, "_").replace(/\s+/g, "_");
  }

  // Le widget affiché est #dash_result_<dash.name> (voir _main_header.html.erb /
  // _main_dash_ajax.erb : "options[:id] = dash_result_#{dash.name}"), PAS
  // #dash_<dash.id> — ce dernier est juste le conteneur widgetloader qui englobe tout,
  // dash_result_<name> est le vrai contenu (titre + données) à imprimer/exporter.
  findWidgetResultEl(event) {
    const name = event.currentTarget.dataset.dashNameParam;
    if (!name) {
      console.warn("dash-name-param manquant sur le bouton d'export.");
      return null;
    }
    return document.getElementById(`dash_result_${name}`);
  }

  exportWidgetPdf(event) {
    event.preventDefault();
    const widgetEl = this.findWidgetResultEl(event);
    if (!widgetEl) return;

    // On capture uniquement #table_scroll_wrapper_<dashId> (le vrai contenu : table
    // ou graphique) et pas tout #dash_result_<name>, qui inclut aussi le titre et le
    // formulaire de filtres (.extend_title) — ni l'un ni l'autre ne doivent apparaître
    // dans le PDF exporté. Ça évite au passage de capturer le groupe d'icônes
    // cog/pdf/xlsx/drag/adjust, qui lui vit dans la barre de titre, hors de ce
    // sous-arbre.
    const dashId = event.currentTarget.dataset.dashIdParam;
    const contentEl = document.getElementById(`table_scroll_wrapper_${dashId}`);
    if (!contentEl) {
      console.warn("Contenu du widget introuvable pour l'export PDF.");
      return;
    }

    this.loadHtml2Canvas(() => {
      this.loadJsPDF(() => {
        const filename = this.getWidgetExportFilename(widgetEl);

        // #table_scroll_wrapper_<dashId> a overflow-y:hidden en dur (voir
        // _main_header.html.erb) et peut être plus petit que son contenu
        // (redimensionnement auto désactivé, widget réduit manuellement...).
        // scrollWidth/scrollHeight donnent la taille réelle du contenu même clippé
        // (contrairement à clientWidth/clientHeight ou au rendu visuel), donc on les
        // passe explicitement à html2canvas plutôt que de toucher au CSS du widget
        // (une tentative précédente qui modifiait temporairement overflow/height
        // perturbait la mise en page gérée par GridStack et produisait un canvas vide).
        const width = contentEl.scrollWidth;
        const height = contentEl.scrollHeight;
        if (!width || !height) {
          console.warn("Widget vide, export PDF annulé.");
          return;
        }
        // Un widget-tableau avec beaucoup de lignes peut produire un très grand
        // canvas ; à scale:2 ça dépasse parfois les limites internes du décodeur PNG
        // de jsPDF, donc on réduit l'échelle en conséquence.
        const scale = width > 1600 || height > 2000 ? 1 : 2;

        html2canvas(contentEl, {
          useCORS: true,
          scale,
          backgroundColor: "#ffffff",
          width,
          height,
          windowWidth: width,
          windowHeight: height,
        })
          .then((canvas) => {
            if (!canvas.width || !canvas.height) {
              throw new Error("Capture du widget vide.");
            }
            // JPEG plutôt que PNG : le décodeur PNG interne de jsPDF échoue de façon
            // non fiable sur certains canvas produits par html2canvas, JPEG ne pose
            // pas ce problème.
            const imgData = canvas.toDataURL("image/jpeg", 0.92);
            const { jsPDF } = window.jspdf;
            // Marge autour du contenu : sans ça la page fait exactement la taille du
            // canvas et le contenu touche les 4 bords, ce qui rend le PDF illisible/mal
            // centré. On agrandit la page de `margin` de chaque côté et on centre l'image
            // dedans.
            const margin = 24;
            const pageWidth = canvas.width + margin * 2;
            const pageHeight = canvas.height + margin * 2;
            const orientation = pageWidth > pageHeight ? "landscape" : "portrait";
            const pdf = new jsPDF({
              orientation,
              unit: "px",
              format: [pageWidth, pageHeight],
            });
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, pageHeight, "F");
            pdf.addImage(imgData, "JPEG", margin, margin, canvas.width, canvas.height);
            pdf.save(`${filename}.pdf`);
          })
          .catch((error) => {
            console.error("Erreur lors de l'export PDF du widget :", error);
          });
      });
    });
  }

  exportWidgetXlsx(event) {
    event.preventDefault();
    const widgetEl = this.findWidgetResultEl(event);
    // Chercher la table dans .table-scroll-wrapper (le vrai contenu, voir
    // _main_header.html.erb) : un simple `querySelector("table")` sur widgetEl
    // récupère en priorité `.barre-titre` (table du titre), qui contient le groupe
    // d'icônes cog/pdf/xlsx/drag/adjust reparenté par widgetloader_controller.js.
    const table = widgetEl?.querySelector(".table-scroll-wrapper table");
    if (!table) {
      console.warn("Aucun tableau trouvé pour l'export Excel.");
      return;
    }

    loadXLSXLibrary(() => {
      const filename = this.getWidgetExportFilename(widgetEl);
      const ws = this.tableToStyledSheet(table);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Feuille1");
      XLSX.writeFile(wb, `${filename}.xlsx`);
    });
  }

  // `XLSX.utils.table_to_book` positionne déjà correctement les colspan/rowspan (via
  // `!merges`) mais ignore complètement le style visuel (fonds de couleur, gras...),
  // qui n'est de toute façon pas supporté à l'écriture par la version communautaire
  // officielle de SheetJS. On reconstruit donc la feuille cellule par cellule à partir
  // du DOM pour reproduire à la fois le positionnement ET le style du tableau source.
  tableToStyledSheet(table) {
    const rows = Array.from(table.rows);
    const grid = [];
    const aoa = [];
    const merges = [];
    const styledCells = [];

    rows.forEach((row, r) => {
      grid[r] = grid[r] || [];
      aoa[r] = aoa[r] || [];
      let c = 0;
      Array.from(row.cells).forEach((cell) => {
        while (grid[r][c]) c++;

        const rowSpan = cell.rowSpan || 1;
        const colSpan = cell.colSpan || 1;

        aoa[r][c] = cell.textContent.trim();
        styledCells.push({ r, c, cell });

        for (let rr = r; rr < r + rowSpan; rr++) {
          grid[rr] = grid[rr] || [];
          aoa[rr] = aoa[rr] || [];
          for (let cc = c; cc < c + colSpan; cc++) {
            grid[rr][cc] = true;
            if (aoa[rr][cc] === undefined) aoa[rr][cc] = "";
          }
        }

        if (rowSpan > 1 || colSpan > 1) {
          merges.push({
            s: { r, c },
            e: { r: r + rowSpan - 1, c: c + colSpan - 1 },
          });
        }

        c += colSpan;
      });
    });

    const maxCols = aoa.reduce((max, row) => Math.max(max, row.length), 0);
    aoa.forEach((row) => {
      for (let i = 0; i < maxCols; i++) if (row[i] === undefined) row[i] = "";
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    if (merges.length) ws["!merges"] = merges;

    styledCells.forEach(({ r, c, cell }) => {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (ws[ref]) ws[ref].s = this.cellStyleFromElement(cell);
    });

    return ws;
  }

  cellStyleFromElement(cell) {
    const computed = window.getComputedStyle(cell);
    const bg = this.cssColorToHex(computed.backgroundColor);
    const color = this.cssColorToHex(computed.color);
    const isBold =
      computed.fontWeight === "bold" || parseInt(computed.fontWeight, 10) >= 600;
    const align = ["left", "center", "right"].includes(computed.textAlign)
      ? computed.textAlign
      : undefined;

    const style = {
      font: { bold: isBold, ...(color ? { color: { rgb: color } } : {}) },
      alignment: { vertical: "center", ...(align ? { horizontal: align } : {}) },
      border: {
        top: { style: "thin", color: { rgb: "D9D9D9" } },
        bottom: { style: "thin", color: { rgb: "D9D9D9" } },
        left: { style: "thin", color: { rgb: "D9D9D9" } },
        right: { style: "thin", color: { rgb: "D9D9D9" } },
      },
    };
    if (bg) style.fill = { patternType: "solid", fgColor: { rgb: bg } };
    return style;
  }

  cssColorToHex(cssColor) {
    const match = cssColor?.match(
      /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/,
    );
    if (!match) return null;
    const [, r, g, b, a] = match;
    if (a !== undefined && parseFloat(a) === 0) return null; // transparent
    const toHex = (n) => Number(n).toString(16).padStart(2, "0").toUpperCase();
    return `${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  toggleFiltrageAuto(event) {
    const checkbox = event.target; // l'input a directement déclenché le "change"
    const itemEl =
      checkbox.closest(".grid-stack-item") ||
      checkbox.closest(".config-dropdown-panel")?._fxGridStackItem;
    if (!itemEl) return;
    const dashSettingId = itemEl.dataset.id;

    const newValue = checkbox.checked ? 1 : 2;
    checkbox.dataset.filtrageAuto = newValue;

    fetch(
      `/${this.currentAppValue}/fdashboards/${dashSettingId}/toggle_filtrage_auto`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            .content,
        },
        body: JSON.stringify({ filtrage_auto: newValue }),
      },
    );

    const dashId =
      itemEl.querySelector("[data-widgetloader-dash-id-value]")?.dataset
        .widgetloaderDashIdValue || dashSettingId;

    document.dispatchEvent(
      new CustomEvent(`filtrage-auto:changed:${dashId}`, {
        detail: { filtrageAuto: newValue },
      }),
    );
  }
  toggleAutoresize(event) {
    event.preventDefault();

    const itemEl =
      event.currentTarget.closest(".grid-stack-item") ||
      event.currentTarget.closest(".config-dropdown-panel")?._fxGridStackItem;
    if (!itemEl) return;
    const dashSettingId = itemEl.dataset.id;
    const checkbox = event.target;

  

    const currentValue = parseInt(checkbox.dataset.autoResize, 10);
    const isEnabled = currentValue === 1;

    checkbox.dataset.autoResize = isEnabled ? 2 : 1;
    checkbox.checked = !isEnabled;

    event.currentTarget.title = isEnabled
      ? "Redimensionnement auto désactivé"
      : "Redimensionnement auto activé";

    const newValue = isEnabled ? 2 : 1;

    fetch(
      `/${this.currentAppValue}/fdashboards/${dashSettingId}/toggle_auto_resize`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            .content,
        },
        body: JSON.stringify({ auto_resize: newValue }),
      },
    );

    if (newValue === 1) {
      this.onWidgetContentUpdated(dashSettingId);
    } else {
      this.setFixedHeight(itemEl);
    }
  }

  // utils déjà utilisés dans ton fichier
  getFullHeight(el) {
    if (!el) return 0;
    const cs = window.getComputedStyle(el);
    return (
      el.offsetHeight +
      parseFloat(cs.marginTop || 0) +
      parseFloat(cs.marginBottom || 0)
    );
  }

  getContentSize(el) {
    if (!el) return { width: 0, height: 0 };
    // cf. le getContentSize local de resizeToContentFull plus haut : ne pas
    // mesurer el.firstElementChild, ça casse dès que le premier enfant
    // n'est pas le contenu visuel réel (ex: un <script> sur les widgets
    // non-tableau) et fait retomber le widget à une taille qui cache tout
    // son contenu.
    return { width: el.scrollWidth, height: el.scrollHeight };
  }
}
